<script>
import PerPageSelect from "@/components/ui/PerPageSelect.vue";

export default {
    components: {PerPageSelect},
    props: ['companyId', 'settlementId', 'pending'],
    data() {
        return {
            data: [],
            loading: true,
            loaded: false,
            meta: {
                current_page: 1,
                total: 0,
                per_page: 0,
            },
            sort: {
                field: 'id',
                order: 'desc'
            },
            controller: null,
            perPage: parseInt(localStorage.getItem('perPage_withdrawalRequests') || '10'),
        }
    },
    methods: {
        loadData() {
            this.loading = true;
            this.controller = new AbortController();
            let params = {
                page: this.meta.current_page,
                per_page: this.perPage,
            };
            if (this.pending) {
                params.status = 'pending';
            }
            if (this.settlementId) {
                params.settlement_id = this.settlementId;
            }
            axios.get('/companies/' + this.companyId + '/withdrawal-requests', {
                signal: this.controller.signal,
                params: params
            }).then(response => {
                this.data = response.data.data;
                this.meta.current_page = response.data.meta.current_page;
                this.meta.total = response.data.meta.total;
                this.meta.per_page = response.data.meta.per_page;
            })
            .catch(error => {})
            .finally(() => {
                this.loaded = true;
                this.loading = false;
            })
        },
        onPageChange(page) {
            document.getElementById('withdrawals-requests-table').scrollIntoView();
            this.controller.abort();
            this.meta.current_page = page;
            this.loadData();
        },
        onSort(field, order) {
            this.controller.abort();
            this.sort = {field, order};
            this.loadData();
        },
        onPerPageChange() {
            localStorage.setItem('perPage_withdrawalRequests', String(this.perPage));
            this.meta.current_page = 1;
            this.loadData();
        },
    },
    beforeUnmount() {
        this.controller ? this.controller.abort() : null;
    },
    mounted() {
        this.loadData();
    }
}
</script>

<template>
    <div class="block has-text-grey has-text-centered mb-4 mt-4" v-if="loaded && data.length === 0">
        {{ $t('withdrawal_requests.labels.no_unsettled_requests') }}
    </div>
    <div id="withdrawals-requests-table"></div>
    <b-table
        v-if="!loaded || (loaded && data.length > 0)"
        :data="loading ? [] : data"
        :loading="loading"

        :paginated="!loading"
        backend-pagination
        :total="loading ? 0 : meta.total"
        :per-page="perPage"
        @page-change="onPageChange"
        :aria-next-label="$t('navigation.next_page')"
        :aria-previous-label="$t('navigation.previous_page')"
        :aria-page-label="$t('navigation.page')"
        :aria-current-label="$t('navigation.current_page')"

        detailed
        :show-detail-icon="true"
        detail-icon="chevron-right"

        backend-sorting
        sort-icon="arrow-up"
        default-sort-direction="asc"
        :default-sort="['created_at', 'desc']"
        @sort="onSort">

        <b-table-column field="name" :label="$t('withdrawal_requests.fields.name.name')" v-slot="props">
            {{ props.row.name }}
        </b-table-column>

        <b-table-column field="bank_account_number" :label="$t('companies.fields.bank_account_number.name')" v-slot="props">
            {{ props.row.bank_account_number }}
        </b-table-column>

        <b-table-column field="money_amount" :label="$t('withdrawal_requests.fields.money_amount.name')" v-slot="props">
            {{ props.row.money_amount }} {{ props.row.currency ?? 'zł' }}
        </b-table-column>

        <b-table-column field="created_at" :label="$t('withdrawal_requests.fields.created_at.name')" v-slot="props">
            <time v-bind:datetime="props.row.created_at" v-bind:title="props.row.created_at">
                {{ props.row.created_at_diff }}
            </time>
        </b-table-column>

        <template #detail="props">
            <article class="media">
                <div class="media-content">
                    <div class="content">
                        <p>
                            <strong class="employee-name is-size-5">
                                {{ props.row.user.first_name }} {{ props.row.user.last_name }}
                            </strong>
                        </p>
                        <div class="fixed-grid has-2-cols has-1-cols-mobile">
                            <div class="grid">
                                <div class="cell">
                                    <p>
                                        <b-icon icon="at"/>
                                        <span class="detail-label">{{ $t('users.fields.email.name') }}:</span>
                                        {{ props.row.user.email }}
                                    </p>
                                    <p v-if="props.row.user.phone_number">
                                        <b-icon icon="phone"/>
                                        <span class="detail-label">{{ $t('users.fields.phone_number.name') }}:</span>
                                        {{ props.row.user.phone_number }}
                                    </p>
                                    <p v-if="props.row.approved_at">
                                        <b-icon icon="calendar-check"/>
                                        <span class="detail-label">{{ $t('withdrawal_requests.fields.approved_at.name') }}:</span>
                                        {{ props.row.approved_at }}
                                    </p>
                                </div>
                                <div class="cell">
                                    <p>
                                        <b-icon icon="bank"/>
                                        <span class="detail-label">{{ $t('users.fields.bank_account_number.name') }}:</span>
                                        {{ props.row.bank_account_number }}
                                    </p>
                                    <p v-if="props.row.user.pesel">
                                        <b-icon icon="account"/>
                                        <span class="detail-label">{{ $t('users.fields.pesel.name') }}:</span>
                                        {{ props.row.user.pesel }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div v-if="props.row.comments">
                            <div>{{ $t('token_transactions.fields.comments.name') }}:</div>
                            <blockquote class="white-space-pre has-border">
                                {{ props.row.comments }}
                            </blockquote>
                        </div>
                    </div>
                </div>
            </article>
        </template>

        <template #bottom-left>
            <PerPageSelect v-model="perPage" @change="onPerPageChange" />
        </template>

    </b-table>

    <loader :loading="loading"/>
</template>
